import {ShippingType} from "@/types/enums";
import {InputData} from "measurement-view-generator/dist/types/types";
import {Address} from "@/types/CheckoutFormData";

export type CheckoutData = {
    hasDifferentBillingAddress: boolean;
    deliveryAddress: Address;
    billingAddress: Address;
    addressChanged: boolean;
    additionalOptions: CartAdditionalOptions;
    productsSpecialWish: { [id: number]: string };
    shipping: number|null;
    payment: number;
    newsletter: boolean|null;
    agb: boolean;
    invoice: Invoice;
    pledgInstallments: string;

    products: {[id: number]: ProductCategory};
    voucherPrice?: string;
    shippingOptions: { [key in ShippingType]?: Option };
    paymentOptions: { [key: number]: Option };
    totalPrice: string;
    totalPriceNumber: number;
    exchangeRate: number;
    subtotal: string;
    subtotalNumber: number;
    productCount: number;
    errors: {[key: string]: any};
    warnings: {[key: string]: any};
    confCoreConfig?: {[key: string]: any};
    configurationJson?: {[key: string]: any};
    redirect?: boolean;
    configurableProductsPrice: number;
    configurableProductsPriceNetto: number;
    nettoPriceNumber?: number;
    taxPriceNumber?: number;
    taxPercentNumber?: number;
    stepsInfo: StepsInfo;
    token: string;
    links: {
        emptyCartReturn: string;
    };
    ecoMobilierPrice?: number;
    currentHeaderBar?: {
        'background-color': string;
        'text-color': string;
        date: {
            from: string;
            to: string;
        };
        id: string;
        text: string;
        voucher: {
            code: string;
            ranges: {
                value: number;
                from: number;
                to: number;
                formattedValue: string;
            }[]
        }
    };
    personalizedCart: {
        title: string;
        text: string;
        advisor: {
            sex: 'm'|'f';
            photo: string;
            name: string;
            position: string;
            phone: string;
            email: string;
        };
        expirationDate: number|null;
        isExpired: boolean;
        mandatoryAssembly: boolean;
    } | null;
    partialPaymentAllowed: boolean;
    partialPayments: string[];
    taxList: TaxListItem[];
    originRoute?: null | string;
};

export type CheckoutServerData = {
    products: {[id: number]: ProductCategory};
    voucherPrice?: string;
    shippingOptions: { [key in ShippingType]?: Option };
    paymentOptions: { [key: number]: Option };
    countries: {[key: string]: Country}|null;
    additionalOptions: { [key: string]: AdditionalOption };
    totalPrice: string;
    totalPriceNumber: number;
    exchangeRate: number;
    subtotal: string;
    subtotalNumber: number;
    productCount: number;
    errors: {[key: string]: any};
    warnings: {[key: string]: any};
    confCoreConfig?: {[key: string]: any};
    configurationJson?: {[key: string]: any};
    redirect?: boolean;
    configurableProductsPrice: number;
    configurableProductsPriceNetto: number;
    nettoPriceNumber?: number;
    taxPriceNumber?: number;
    taxPercentNumber?: number;
    stepsInfo: StepsInfo;
    token: string;
    links: {
        emptyCartReturn: string;
    };
    ecoMobilierPrice?: number;
    currentHeaderBar?: {
        'background-color': string;
        'text-color': string;
        date: {
            from: string;
            to: string;
        };
        id: string;
        text: string;
        voucher: {
            code: string;
            ranges: {
                value: number;
                from: number;
                to: number;
                formattedValue: string;
            }[]
        }
    };
    personalizedCart: {
        title: string;
        text: string;
        advisor: {
            sex: 'm'|'f';
            photo: string;
            name: string;
            position: string;
            phone: string;
            email: string;
        };
        expirationDate: number|null;
        isExpired: boolean;
        mandatoryAssembly: boolean;
    } | null;
    partialPaymentAllowed: boolean;
    partialPayments: string[];
    taxList: TaxListItem[];
    customer: {
        hasDifferentBillingAddress: boolean;
        deliveryAddress: Address;
        billingAddress: Address;
        addressChanged: boolean;
    };
    cart: {
        additionalOptions: CartAdditionalOptions;
        productsSpecialWish: { [id: number]: string };
        shipping: ShippingType;
        payment: number;
        newsletter: boolean|null;
        agb: boolean;
        invoice: Invoice;
        pledgInstallments: string;
    };
}

export enum ProductButton {
    Edit = 'edit',
    Copy = 'copy',
    Delete = 'unset',
}

export type ProductCategory = {
    name: string;
    isDefault: boolean;
    products: {[id: number]: Product};
}

export type Product = {
    id: string|number;
    name: string;
    description?: string;
    price: string;
    priceNumber?: number;
    buttons: Array<ProductButton>;
    image?: string;
    color?: string | null;
    images?: { [key: string]: { src: string; thumbnail_s: string; } };
    delivery?: Delivery;
    specialWish?: string;
    specialWishEnabled?: boolean;
    shelfJson?: string;
    confCoreJson?: string;
    configurationJson?: InputData;
    generateImage: false;
    type: string;
    classification: string;
    from_pfc?: number;
    product_detail_file_url?: null | string;
    files?: Array<File>;
    bulletpoints: Array<string>;
    sort_index?: number;
}

type Delivery = {
    from: number;
    to: number;
}

export type Option = {
    id?: number;
    label: string;
    module: string;
    sublabel?: string;
    price: string;
    priceNumber?: string;
    hide?: boolean;
    default?: boolean;
    additionalNote?: string;
    hideIconInSelect?: boolean;
    sublabelSelected?: string;
    boxIconUrl?: null | string;
    sidebarIconUrl?: null | string;
    birthdayRequired: boolean;
    creditCheck: boolean;
    cardDataRequired: boolean;
    isLocked?: boolean;
    partialPaymentAllowed?: boolean;
    sort_index?: number;
}

export type StepsInfo = {
    address: string;
    shipping: ShippingStepInfo;
    payment: string;
}

export type ShippingStepInfo = {
    price: string;
    freeDelivery: boolean;
}

export type CartAdditionalOptions = {
    assembly: boolean;
    assemblyData: {
        canOrder: boolean;
        price: string;
        priceNumber: number;
        countries?: string[];
    };
    hasVoucher: boolean;
    voucher: {
        code: undefined|string;
        price: undefined|string;
        priceNumber: undefined|number;
        campaignValidUntil: undefined|string;
    };
    trustedShops: boolean;
    hasSpecialWish: boolean;
    specialWish: string;
}

export type Invoice = {
    birthDate: {
        day: string|null;
        month: string|null;
        year: string|null
    }
}

export type AdditionalOptions = {
    assembly: AdditionalOption;
    shipping: AdditionalOption;
    voucher: AdditionalOption & {campaignValidUntil?: string};
    trustedShops: AdditionalOptionTS;
}

export type AdditionalOption = {
    code: string;
    price: string;
    priceNumber?: number;
    canOrder?: boolean;
    countries?: number[];
}

export type AdditionalOptionTS = {
    price: string;
    maxCoverHeader: string;
}

export enum ErrorCodes {
    GENERAL = 'ERROR',
    VOUCHER = 'voucher',
    REDIRECT = 'show_after_redirect',
}

export type Country = {
    code: string;
    name: string;
    phoneCode: string;
}

export type File = {
    name: string;
    url: string;
    mime: string;
}

export type Image = {
    name: string;
    url: string;
}

export type TaxListItem = {
    tax_rate: number;
    total_tax: string;
    total_netto: string;
    translation: string;
}

export default CheckoutServerData;
